<template>
<section :class="{'has-pagination': pagination}" class="data-table-outer">
  <section ref="data-table-wrapper" class="data-table-wrapper">
    <section
      :style="{
      width: `${padding[3]}px`
    }"
      class="data-table-fixed-header"
    >
      <table
        v-if="fixedColumns.length > 0"
      >
        <colgroup>
          <col
            v-for="column in fixedColumns"
            :key="column.id"
            ref="fixed-head-col"
            :width="column.width"
          />
        </colgroup>
        <thead>
        <tr ref="fixed-head-row">
          <th
            v-for="column in fixedColumns"
            :key="column.id"
            :align="column.align || 'left'"
            v-html="column.name"
          ></th>
        </tr>
        </thead>
      </table>
    </section>

    <section
      ref="normal-head-wrapper"
      :style="{
        'padding-left': `${padding[3]}px`
      }"
      :class="{
        'no-left-border': fixedColumns.length > 0
      }"
      class="data-table-normal-header"
      @mousewheel.prevent="onNormalHeadWrapperMouseWheel"
    >
      <table ref="normal-head-table">
        <colgroup>
          <col
            v-for="column in normalColumns"
            :key="column.id"
            ref="normal-head-col"
            :width="column.width"
          />
        </colgroup>
        <thead>
        <tr ref="normal-head-row">
          <th
            v-for="column in normalColumns"
            :key="column.id"
            :align="column.align || 'left'"
            v-html="column.name"
          ></th>
        </tr>
        </thead>
      </table>
    </section>

    <section
      ref="fixed-body-wrapper"
      :style="{
      width: `${padding[3]}px`,
      'padding-top': `${padding[0]}px`
    }"
      class="data-table-fixed-body"
      @mousewheel.prevent="onFixedBodyWrapperMouseWheel"
    >
      <table
        v-if="fixedColumns.length > 0"
      >
        <colgroup>
          <col
            v-for="column in fixedColumns"
            :key="column.id"
            ref="fixed-body-col"
            :width="column.width"
          />
        </colgroup>
        <tbody>
        <tr
          v-for="(row, index) in filterData"
          :key="index"
          ref="fixed-body-rows"
          :class="{
          'hover-row': index === hoverRowIndex,
          'selected-row': index === selectedRowIndex
        }"
          @mouseover="onRowHover(row, index)"
          @mouseout="onRowMouseout(row, index)"
          @click="onRowClick(row, index)"
          @dblclick="onRowDblclick(row, index)"
        >
          <td
            v-for="column in fixedColumns"
            :key="column.id"
            :align="column.align || 'left'"
            v-html="row[column.id]"
          ></td>
        </tr>
        </tbody>
      </table>
    </section>

    <section
      ref="data-table-content-wrapper"
      :style="{
        padding: `${padding.join('px ')}px`
      }"
      :class="{
        'no-left-border': fixedColumns.length > 0
      }"
      class="data-table-normal-body"
      @scroll="onNormalBodyScroll"
    >
      <table
        ref="normal-body-table"
      >
        <colgroup>
          <col
            v-for="column in normalColumns"
            :key="column.id"
            ref="normal-body-col"
            :width="column.width"
          />
        </colgroup>
        <tbody>
        <tr
          ref="normal-body-rows"
          v-for="(row, index) in filterData"
          :key="index"
          :class="{
            'hover-row': index === hoverRowIndex,
            'selected-row': index === selectedRowIndex,
            highlight: row.dataTime === highlightDataTime
          }"
          @mouseover="onRowHover(row, index)"
          @mouseout="onRowMouseout(row, index)"
          @click="onRowClick(row, index)"
          @dblclick="onRowDblclick(row, index)"
        >
          <td
            v-for="column in normalColumns"
            :key="column.id"
            :align="column.align || 'left'"
            v-html="row[column.id]"
          ></td>
        </tr>
        </tbody>
      </table>
    </section>
  </section>

  <section v-if="pagination" class="pagination-wrapper">
    <section class="pagination-content">
      <span style="color:#606266;">共<span style="color:#409EFF"> {{data.length}} </span>条</span>
      <ul class="page-no-list">
        <li
          :disabled="paginationConfig.no === 1"
          class="page-no"
          @click="onSwitchPageBtnClick(-1)"
        >&lt;</li>
        <li
          v-for="pageNo in paginationConfig.pageNos"
          :key="pageNo"
          :class="{selected: paginationConfig.no === pageNo}"
          class="page-no"
          @click="onPageNoBtnClick(pageNo)"
        >{{pageNo}}</li>
        <li
          :disabled="paginationConfig.no === paginationConfig.totalPage"
          class="page-no"
          @click="onSwitchPageBtnClick(1)"
        >&gt;</li>
      </ul>
    </section>
  </section>
</section>
</template>

<script>
export default {
  name: 'DataTable',
  props: {
    columns: {
      required: true,
      type: Array
    },
    data: {
      required: true,
      type: Array
    },
    highlightDataTime: {},
    selectedStyle: {
      type: Boolean,
      default: false
    },
    pagination: {}
  },
  data () {
    return {
      padding: [0, 0, 0, 0],
      hoverRowIndex: -1,
      selectedRowIndex: -1
    }
  },
  computed: {
    fixedColumns () {
      return this.columns.filter(column => column.fixed)
    },
    normalColumns () {
      return this.columns.filter(column => !column.fixed)
    },
    filterData () {
      let {data, pagination} = this

      if (pagination) {
        let {size} = pagination
        let index = ((pagination.no - 1) * size)
        return data.slice(index, index + size)
      } else {
        return data
      }
    },
    paginationConfig () {
      let {pagination: {no, size}} = this
      let totalPage = Math.ceil(this.data.length / size)
      let pageNos = []
      let start = Math.max(no - 4, 1)
      let end = Math.min(start + 8, totalPage)

      start = Math.max(end - 8, 1)

      for (let i = start; i <= end; i++) {
        pageNos.push(i)
      }

      return {
        no,
        size,
        totalPage,
        pageNos
      }
    }
  },
  methods: {
    doLayout () {
      console.time('DataTable')
      let padding = this.padding = [0, 0, 0, 0]
      let {fixedColumns, normalColumns} = this
      let dataTableContentWrapper = this.getTableContentWrapper()

      if (fixedColumns.length > 0) {
        padding.splice(3, 1, fixedColumns.reduce((totalWidth, fixedColumn) => totalWidth + fixedColumn.width, 0))

        this.$nextTick(() => {
          let headRowHeights = []
          let bodyRowHeights = []
          let fixedHeadRow = this.getFixedHeadRow()
          let normalHeadRow = this.getNormalHeadRow()
          let fixedBodyRows = this.getFixedBodyRows()
          let normalBodyRows = this.getNormalBodyRows()
          let dataTableWrapper = this.getDataTableWrapper()

          dataTableWrapper.style.contentVisibility = 'hidden'

          padding.splice(0, 1, this.setHeight([fixedHeadRow], [normalHeadRow], headRowHeights) - 1)
          this.setHeight(fixedBodyRows, normalBodyRows, bodyRowHeights)

          this.$el.removeChild(dataTableWrapper)

          fixedHeadRow.style.height = normalHeadRow.style.height = `${headRowHeights[0]}px`
          bodyRowHeights.forEach((height, index) => {
            fixedBodyRows[index].style.height = normalBodyRows[index].style.height = `${height}px`
          })

          this.$el.insertAdjacentElement('afterbegin', dataTableWrapper)
          dataTableWrapper.style.contentVisibility = 'visible'

          this.setLayoutHeight(dataTableContentWrapper, this.getFixedBodyWrapper())
        })
      } else {
        padding.splice(0, 1, this.getNormalHeadWrapper().offsetHeight - 1)
      }

      let normalWidth = normalColumns.reduce((totalWidth, fixedColumn) => totalWidth + fixedColumn.width, 0)
      this.getNormalHeadTable().width = normalWidth
      this.getNormalBodyTable().width = normalWidth

      setTimeout(() => {
        this.setLayoutWidth(dataTableContentWrapper, this.getNormalHeadWrapper())
      }, 0)

      console.timeEnd('DataTable')
    },
    setHeight (fixedRows, normalRows, rowHeights) {
      return fixedRows.reduce((totalHeight, fixedRow, index) => {
        let normalRow = normalRows[index]
        let height = Math.max(fixedRow.offsetHeight, normalRow.offsetHeight)
        rowHeights.push(height)
        return totalHeight + height
      }, 0)
    },
    setLayoutHeight (dataTableContentWrapper, fixedBodyWrapper) {
      fixedBodyWrapper.style.height = `${dataTableContentWrapper.clientHeight}px`
    },
    setLayoutWidth (dataTableContentWrapper, normalHeadWrapper) {
      normalHeadWrapper.style.width = `${dataTableContentWrapper.clientWidth}px`
    },
    setRowSelectStyle (index) {
      if (this.selectedStyle) {
        this.selectedRowIndex = this.selectedRowIndex === index ? -1 : index
      }
    },
    clearResizeTimer () {
      clearTimeout(this.resizeTimer)
    },
    onNormalBodyScroll (event) {
      let normalBodyWrapper = this.getTableContentWrapper()

      if (this.fixedColumns.length > 0) {
        let fixedBodyWrapper = this.getFixedBodyWrapper()
        let scrollTop = normalBodyWrapper.scrollTop
        fixedBodyWrapper.scrollTop = scrollTop
      }

      let normalHeadWrapper = this.getNormalHeadWrapper()
      let scrollLeft = normalBodyWrapper.scrollLeft
      normalHeadWrapper.scrollLeft = scrollLeft
    },
    onNormalHeadWrapperMouseWheel (event) {
      let normalBodyWrapper = this.getTableContentWrapper()
      let normalHeadWrapper = this.getNormalHeadWrapper()
      let scrollLeft = normalBodyWrapper.scrollLeft + event.deltaX
      normalBodyWrapper.scrollLeft = normalHeadWrapper.scrollLeft = scrollLeft
    },
    onFixedBodyWrapperMouseWheel (event) {
      let normalBodyWrapper = this.getTableContentWrapper()
      let fixedBodyWrapper = this.getFixedBodyWrapper()
      let scrollTop = normalBodyWrapper.scrollTop + event.deltaY
      normalBodyWrapper.scrollTop = fixedBodyWrapper.scrollTop = scrollTop
    },
    onRowHover (row, index) {
      this.hoverRowIndex = index
      this.$emit('row-hover', row)
    },
    onRowMouseout (row, index) {
      this.hoverRowIndex = -1
      this.$emit('row-mouseout', row)
    },
    onRowClick (row, index) {
      this.setRowSelectStyle(index)
      this.$emit('row-click', row)
    },
    onRowDblclick (row, index) {
      this.setRowSelectStyle(index)
      this.$emit('row-dblclick', row)
    },
    onWindowResize () {
      this.clearResizeTimer()
      this.resizeTimer = setTimeout(() => {
        this.doLayout()
      }, 100)
    },
    onPageNoBtnClick (pageNo) {
      let {pagination} = this

      if (pagination.no === pageNo) {
        return
      }

      pagination.no = pageNo
      this.$emit('pagination-no-changed', pageNo)
      this.$nextTick(() => {
        this.doLayout()
      })
    },
    onSwitchPageBtnClick (direction) {
      let pageNo = this.pagination.no + direction

      if (pageNo < 1 || pageNo > this.paginationConfig.totalPage) {
        return
      }

      this.onPageNoBtnClick(pageNo)
    },
    getNormalHeadTable () {
      return this.$refs['normal-head-table']
    },
    getNormalBodyTable () {
      return this.$refs['normal-body-table']
    },
    getFixedHeadRow () {
      return this.$refs['fixed-head-row']
    },
    getNormalHeadRow () {
      return this.$refs['normal-head-row']
    },
    getFixedBodyRows () {
      return this.$refs['fixed-body-rows'] || []
    },
    getNormalBodyRows () {
      return this.$refs['normal-body-rows'] || []
    },
    getNormalHeadWrapper () {
      return this.$refs['normal-head-wrapper']
    },
    getFixedBodyWrapper () {
      return this.$refs['fixed-body-wrapper']
    },
    getTableContentWrapper () {
      return this.$refs['data-table-content-wrapper']
    },
    getDataTableWrapper () {
      return this.$refs['data-table-wrapper']
    }
  },
  created () {
    window.addEventListener('resize', this.onWindowResize, true)
  },
  beforeDestroy () {
    this.clearResizeTimer()
    window.removeEventListener('resize', this.onWindowResize, true)
  }
}
</script>

<style lang="scss" scoped>
.data-table-outer {
  $PAGINATION-HEIGHT: 40px;

  position: relative;
  width: 100%;
  height: 100%;

  &.has-pagination {
    padding-bottom: $PAGINATION-HEIGHT;
  }

  .pagination-wrapper {
    position: absolute;
    font-size: 14px;
    bottom: 0;
    left: 0;
    width: 100%;
    height: $PAGINATION-HEIGHT;
    padding-top: 10px;
  }

  .pagination-content {
    float: right;
    height: 100%;
    line-height: 30px;
  }

  .page-no-list {
    float: right;
    height: 100%;
    margin: 0 0 0 1em;
    padding: 0;
    font-weight: 500;
    user-select: none;

    .page-no {
      float: left;
      width: 30px;
      margin: 0 1px;
      text-align: center;
      color: #606266;
      background-color: #f4f4f5;
      cursor: pointer;

      &.selected {
        color: #ffffff;
        background-color: #409eff;
      }

      &[disabled="disabled"] {
        color: #c0c4cc;
        cursor: not-allowed;
      }
    }
  }
}

.data-table-wrapper {
  position: relative;
  font-size: .875rem;
  width: 100%;
  height: 100%;
  border: 1px solid #EBEEF5;
  /*content-visibility: hidden;*/
  /*content-visibility: auto;*/
  overflow: hidden;

  .data-table-fixed-header,
  .data-table-normal-header,
  .data-table-fixed-body {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    overflow: hidden;
  }

  .data-table-fixed-header,
  .data-table-normal-header {
    border: {
      /*top: 1px solid #EBEEF5;*/
      bottom: 1px solid #EBEEF5;
    };
    background-color: #f9f9f9;
  }

  .data-table-fixed-body {
    background-color: #ffffff;
  }

  .data-table-fixed-header {
    z-index: 2;
  }

  .data-table-normal-body {
    width: 100%;
    height: 100%;
    overflow: auto;
  }

  table {
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;
  }

  tbody {
    tr {
      &:nth-child(2n) {
        background-color: #fafafa;
      }

      &:last-child {
        td {
          border-bottom: 1px solid #EBEEF5;
          line-height: 1.5714285714285714;
        }
      }

      &.hover-row {
        background-color: #f5f7fa;
      }

      &.selected-row {
        background-color: #ecf5ff;
      }

      &.highlight {
        td {
          color: #409EFF;
        }
      }
    }
  }

  th,
  td {
    padding: 6px 10px;
    line-height: 1.6428571428571428;

    &:not(:first-child) {
      border-left: 1px solid #EBEEF5;
    }

    &:last-child {
      border-right: 1px solid #EBEEF5;
    }
  }

  th {
    color: #303030;
  }

  td {
    border-top: 1px solid #EBEEF5;
    color: #606266;
  }

  /*.data-table-fixed-body,*/
  /*.data-table-normal-body {*/
  /*  &:after {*/
  /*    content: "";*/
  /*    display: block;*/
  /*    height: 10px;*/
  /*  }*/
  /*}*/

  .no-left-border {
    th,
    td {
      &:first-child {
        border-left: 0;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Data/DataTable.vue